import { useState } from "react";
import { AlertOctagon, CheckCircle2, Clock, LogIn, Undo2 } from "lucide-react";

import { Button } from "@/components/ui/button";

// ─── LeavingSiteCloseoutModal ──────────────────────────────────────────
//
// Pops on the field job-detail screen when:
//   - useGpsDeparture has fired (tech moved outside the site geofence
//     for >= grace seconds), OR
//   - the office hit "Ask Tech to Close Out" on the dispatch missed-jobs
//     modal — push notification deep-links into the job with
//     ?openCloseout=1 which forces this modal open immediately.
//
// The tech never decides a job is a wash — that's an office call (Issue #454).
// So the tech's non-complete options are both NON-terminal "return needed":
//   1. Mark Complete            — the work is done; tech forgot to tap Complete.
//   2. Couldn't start           — arrived but couldn't begin (locked gate, nobody
//                                  on site). Ends the trip, sends to office.
//   3. Couldn't finish          — got partway; needs another trip. Ends the trip,
//                                  sends to office.
//   4. Not yet                  — still coming back; audit ping + 30-min snooze.
// Both 2 and 3 hit POST /jobs/:id/return-needed → office_review + Return-Needed
// strip; the OFFICE then decides add-another-appointment vs cannot-complete.

export type LeavingSiteResolution =
    | { kind: "complete" }
    | { kind: "returnNeeded"; reason: string; startKind: "couldnt_start" | "couldnt_finish" }
    | { kind: "notYet" };

interface LeavingSiteCloseoutModalProps {
    open: boolean;
    /** Office's optional "please close this out" note (if the modal was
     *  triggered by a request-closeout push instead of geofence). */
    officeNote?: string | null;
    /** Office user who pinged the tech (if applicable). */
    officeNoteFromName?: string | null;
    /** Reason the modal is open: lets us tweak the headline copy. */
    trigger: "geofence" | "office_request";
    busy?: boolean;
    onResolve: (res: LeavingSiteResolution) => void;
    onDismiss: () => void;
}

export function LeavingSiteCloseoutModal({
    open,
    officeNote,
    officeNoteFromName,
    trigger,
    busy,
    onResolve,
    onDismiss,
}: LeavingSiteCloseoutModalProps) {
    const [mode, setMode] = useState<"menu" | "returnNeeded">("menu");
    const [startKind, setStartKind] = useState<"couldnt_start" | "couldnt_finish">("couldnt_finish");
    const [reason, setReason] = useState("");

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-amber-300 bg-card shadow-2xl">
                <header className="flex items-start gap-3 px-4 py-4 border-b border-border/60 bg-amber-50 dark:bg-amber-950/40">
                    <div className="rounded-full bg-amber-500/20 p-2 shrink-0">
                        <AlertOctagon className="h-5 w-5 text-amber-700 dark:text-amber-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-foreground">
                            {trigger === "office_request"
                                ? "Office needs you to close this out"
                                : "Closing out before you go?"}
                        </h2>
                        <p className="text-xs text-foreground/80 mt-0.5">
                            {trigger === "office_request"
                                ? "Pick what happened with this job so it can leave the missed list."
                                : "We noticed you've left the site. Lock in the result before the day rolls over."}
                        </p>
                    </div>
                </header>

                {trigger === "office_request" && officeNote && (
                    <div className="mx-4 mt-3 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                            Note from {officeNoteFromName || "office"}
                        </p>
                        <p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap break-words">{officeNote}</p>
                    </div>
                )}

                <div className="p-4 space-y-3">
                    {mode === "menu" && (
                        <div className="space-y-2">
                            <Button
                                className="w-full justify-start gap-3 h-12"
                                disabled={busy}
                                onClick={() => onResolve({ kind: "complete" })}
                            >
                                <CheckCircle2 className="h-4 w-4" />
                                <span className="flex flex-col items-start">
                                    <span>Mark Complete</span>
                                    <span className="text-[11px] opacity-80">
                                        Work is done — submit for office review
                                    </span>
                                </span>
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full justify-start gap-3 h-12"
                                disabled={busy}
                                onClick={() => {
                                    setReason("");
                                    setStartKind("couldnt_start");
                                    setMode("returnNeeded");
                                }}
                            >
                                <LogIn className="h-4 w-4" />
                                <span className="flex flex-col items-start">
                                    <span>Couldn't start — return needed</span>
                                    <span className="text-[11px] opacity-80">
                                        Locked out, nobody on site — send to office
                                    </span>
                                </span>
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full justify-start gap-3 h-12"
                                disabled={busy}
                                onClick={() => {
                                    setReason("");
                                    setStartKind("couldnt_finish");
                                    setMode("returnNeeded");
                                }}
                            >
                                <Undo2 className="h-4 w-4" />
                                <span className="flex flex-col items-start">
                                    <span>Couldn't finish — send to office</span>
                                    <span className="text-[11px] opacity-80">
                                        Got partway; office picks a return day
                                    </span>
                                </span>
                            </Button>
                            <button
                                type="button"
                                className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs text-foreground/70 hover:bg-secondary/60"
                                disabled={busy}
                                onClick={() => onResolve({ kind: "notYet" })}
                            >
                                <Clock className="h-3.5 w-3.5" />
                                Not yet — I'm coming back
                            </button>
                        </div>
                    )}

                    {mode === "returnNeeded" && (
                        <ReasonForm
                            label={
                                startKind === "couldnt_start"
                                    ? "What kept you from starting?"
                                    : "What does the office need to know to reschedule?"
                            }
                            placeholder={
                                startKind === "couldnt_start"
                                    ? "Gate locked, nobody on site, wrong address…"
                                    : "Need bigger truck / parts on order / call before next visit"
                            }
                            value={reason}
                            onChange={setReason}
                            busy={busy}
                            onCancel={() => setMode("menu")}
                            onSubmit={() =>
                                onResolve({
                                    kind: "returnNeeded",
                                    reason: reason.trim(),
                                    startKind,
                                })
                            }
                            primaryLabel="Send to office"
                        />
                    )}
                </div>

                {trigger === "geofence" && mode === "menu" && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="block w-full text-center px-4 py-2 text-[11px] text-foreground/60 hover:text-foreground transition-colors"
                    >
                        Hide for now
                    </button>
                )}
            </div>
        </div>
    );
}

function ReasonForm({
    label,
    placeholder,
    value,
    onChange,
    busy,
    onCancel,
    onSubmit,
    primaryLabel,
}: {
    label: string;
    placeholder: string;
    value: string;
    onChange: (v: string) => void;
    busy?: boolean;
    onCancel: () => void;
    onSubmit: () => void;
    primaryLabel: string;
}) {
    const trimmed = value.trim();
    return (
        <div className="space-y-2">
            <label className="block text-xs font-medium text-foreground/80">{label}</label>
            <textarea
                rows={3}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                autoFocus
            />
            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
                    Back
                </Button>
                <Button size="sm" onClick={onSubmit} disabled={busy || trimmed.length === 0}>
                    {primaryLabel}
                </Button>
            </div>
        </div>
    );
}
